/* eslint-disable react/jsx-boolean-value */
import { Button, RadioButton, TextArea, TextInput } from '@pmi/dsm-react';
import { useFormik } from 'formik';
import { Alert, Form } from 'react-bootstrap';
import * as Yup from 'yup';
import { NewWindowIcon } from '@pmi/dsm-react/dist/components/icons/16';
import { AlertInfoSolidIcon } from '@pmi/dsm-react/dist/components/icons';
import EmbedVideo from '../../../shared/components/EmbedVideo/EmbedVideo';
import AccountDto from '../../../shared/models/AccountModel';
import { EditWelcomeMessageFormModel } from './models';
import { PMI_WELCOME_VIDEO_URL } from '../../../shared/constants';
import getEmbedVideoLink from '../../../shared/components/EmbedVideo/getEmbedVideoLink';
import styles from './welcomeMessageCard.module.scss';
import MarkdownEditor from '../../../shared/components/MarkdownEditor';
import {
  defaultWelcomeContent,
  defaultWelcomeTitle,
} from '../../../shared/utils/defaultTexts';

function CustomInfoAlert() {
  return (
    <Alert variant="info" className="p-2 m-0">
      <div className="d-flex">
        <AlertInfoSolidIcon
          color="success"
          fontSize="sm"
          className="d-flex align-self-start pe-3 pt-1"
        />
        <div className="d-flex flex-column justify-content-start align-items-start">
          <p className={`mb-0 lh-sm ${styles.infoAlertText}`}>
            Copy your video’s URL from either Youtube or Vimeo and paste below.
            To make sure your video is accessible to all users, we recommend you
            include closed captions, audio descriptions, and include a text
            transcript.
          </p>
          <div className="flex">
            <a
              href="https://www.w3.org/WAI/media/av/"
              target="_blank"
              rel="noreferrer"
              className={`${styles.infoAlertText} lh-sm`}
            >
              Learn more about video accessibility
            </a>
            <NewWindowIcon
              color="original"
              className={styles.infoAlertSvgIcon}
              fontSize="xs"
            />
          </div>
        </div>
      </div>
    </Alert>
  );
}

export default function EditWelcomeMessageForm({
  model,
  saveWelcomeContent,
}: {
  model: AccountDto;
  saveWelcomeContent: (model: EditWelcomeMessageFormModel) => void;
}) {
  const initialData: EditWelcomeMessageFormModel = {
    title: model.welcome?.title || defaultWelcomeTitle,
    content: model.welcome?.content || defaultWelcomeContent,
    customVideo: !!model.welcome?.video,
    videoUrl: model.welcome?.video?.videoUrl,
    transcript: model.videoTranscriptData,
  };
  const validationSchema = Yup.object<EditWelcomeMessageFormModel>().shape({
    title: Yup.string().required('Title is Required'),
    content: Yup.string().required('Content is Required'),
    customVideo: Yup.boolean().required('Video type is required'),
    videoUrl: Yup.string().test('videoUrl_required', (value, context) => {
      const formModel = context.parent as EditWelcomeMessageFormModel;
      if (formModel.customVideo && !value?.trim()?.length)
        return context.createError({ message: 'Video URL is required' });
      if (
        formModel.customVideo &&
        value?.trim()?.length &&
        !getEmbedVideoLink(value)
      )
        return context.createError({
          message:
            'Invalid Video URL, Only Youtube and Vimeo video links are supported',
        });
      return true;
    }),
  });

  const formik = useFormik({
    initialValues: initialData,
    onSubmit: (values: EditWelcomeMessageFormModel) => {
      saveWelcomeContent(values);
    },
    validationSchema,
  });

  const onUpdateClick = () => {
    formik.submitForm();
  };
  return (
    <div className="row row-cols-1 gy-4">
      <div className="col">
        <div className="row gy-3">
          <div className="col">
            <div className="row gy-3 row-cols-1">
              <div className="col">
                <Form.Group
                  controlId="title"
                  className={`${formik.errors.title ? 'is-invalid' : ''}`}
                >
                  <Form.Label className="is-required">Title</Form.Label>
                  <TextInput
                    name="title"
                    value={formik.values.title}
                    className={`${formik.errors.title ? 'is-invalid' : ''}`}
                    onChange={formik.handleChange}
                    isInvalid={!!formik.errors.title}
                    validationMessage={formik.errors.title}
                    maxLength={100}
                  />
                  {formik.values.title?.length >= 50 && (
                    <span
                      className={`${
                        formik.errors.title ? 'text-danger' : ''
                      } d-inline-block w-100 text-end small`}
                    >{`${(
                      100 - formik.values.title.length
                    )?.toLocaleString()} characters remaining`}</span>
                  )}
                </Form.Group>
              </div>
              <div className="col">
                <Form.Group
                  controlId="content"
                  className={`${formik.errors.content ? 'is-invalid' : ''}`}
                >
                  <Form.Label className="is-required">Content</Form.Label>
                  <MarkdownEditor
                    onChange={(val) => formik.setFieldValue('content', val)}
                    value={formik.values.content}
                  />
                  {formik.values.content?.length >= 1000 && (
                    <span
                      className={`${
                        formik.errors.content ? 'text-danger' : ''
                      } d-inline-block w-100 text-end small`}
                    >{`${(
                      1500 - formik.values.content.length
                    )?.toLocaleString()} characters remaining`}</span>
                  )}
                  {formik.errors.content ? (
                    <div className="invalid-feedback">
                      {formik.errors.content}
                    </div>
                  ) : null}
                </Form.Group>
              </div>
            </div>
          </div>
          <div className="col">
            <div className="row row-cols-1 gy-1">
              <div className="col">
                <Form.Group
                  controlId="customVideo"
                  className={`${
                    formik.errors.customVideo ? 'is-invalid' : ''
                  } d-flex flex-column`}
                >
                  <div className="d-flex">
                    <div className="pe-5">
                      <RadioButton
                        id="pmiVideoOption"
                        label="Use PMI's Welcome Video"
                        value={false}
                        name="customVideo"
                        checked={formik.values.customVideo === false}
                        onChange={() => {
                          formik.setFieldValue('customVideo', false);
                          formik.setFieldValue('videoUrl', '');
                          formik.setFieldValue('transcript', '');
                        }}
                      />
                    </div>
                    <div>
                      <RadioButton
                        id="customVideoOption"
                        label="Use a Custom Video"
                        value={true}
                        name="customVideo"
                        checked={formik.values.customVideo === true}
                        onChange={() => {
                          formik.setFieldValue('customVideo', true);
                        }}
                      />
                    </div>
                  </div>
                  {formik.errors.customVideo ? (
                    <div className="invalid-feedback">
                      {formik.errors.customVideo}
                    </div>
                  ) : null}
                </Form.Group>
              </div>
              {formik.values.customVideo === false && (
                <div className="col">
                  <EmbedVideo
                    url={PMI_WELCOME_VIDEO_URL}
                    transcript={formik.values.transcript}
                  />
                </div>
              )}
              {formik.values.customVideo && (
                <div className="col">
                  <div className="row row-cols-1 gy-0">
                    <div className="col">
                      <CustomInfoAlert />
                    </div>
                    <div className="col mt-2">
                      <Form.Group
                        controlId="videoUrl"
                        className={`mt-1 ${
                          formik.errors.videoUrl ? 'is-invalid' : ''
                        }`}
                      >
                        <Form.Label className="is-required">
                          Video URL
                        </Form.Label>
                        <TextInput
                          name="videoUrl"
                          value={formik.values.videoUrl}
                          className={`${
                            formik.errors.videoUrl ? 'is-invalid' : ''
                          }`}
                          onChange={formik.handleChange}
                          isInvalid={!!formik.errors.videoUrl}
                          validationMessage={formik.errors.videoUrl}
                          maxLength={100}
                        />
                      </Form.Group>
                    </div>
                    <div className="col mt-2">
                      <Form.Group
                        controlId="transcript"
                        className={`mt-1 ${
                          formik.errors.transcript ? 'is-invalid' : ''
                        }`}
                      >
                        <Form.Label>Video Transcript Text</Form.Label>
                        <TextArea
                          name="transcript"
                          value={formik.values.transcript}
                          className={`${
                            formik.errors.transcript ? 'is-invalid' : ''
                          }`}
                          onChange={formik.handleChange}
                          isInvalid={!!formik.errors.transcript}
                          validationMessage={formik.errors.transcript}
                          showWordsCount={false}
                          maxWords={0}
                          minWords={0}
                        />
                      </Form.Group>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="col">
        <Button
          titleText={
            formik.isSubmitting ? 'Updating..' : 'Update Welcome Message'
          }
          onClick={onUpdateClick}
          disabled={formik.isSubmitting}
        />
      </div>
    </div>
  );
}
